import * as React from 'react';
import { useEffect, useState } from 'react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation, useParams } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { LoadingSection } from '@coursera/cds-core';

import AuthenticationModal from 'bundles/authentication/shared/components/authentication-modal/AuthenticationModal';
import { constructLaunchUrl } from 'bundles/launched-lab/utils/LabUrlUtils';
import { getWorkspaceLaunchUrl } from 'bundles/launched-lab/utils/WorkspaceApiUtils';

import _t from 'i18n!nls/workspaces-app';

type PropsFromRouter = {
  workspaceId: string;
  path?: string;
  forceRefresh?: boolean;
  isIframe?: boolean;
  restOfQuery: Record<string, string>;
};

const useWorkspaceLaunchUrl = () => {
  const isAuthenticated = isAuthenticatedUser();
  const [hasError, setHasError] = useState<boolean>(false);
  const [launchUrl, setLaunchUrl] = useState<string>('');

  const { workspaceId } = useParams();

  const location = useLocation();
  const {
    query: { path, forceRefresh, isIframe, ...restOfQuery },
  } = location;

  const propsFromRouter: PropsFromRouter = {
    workspaceId,
    path: simplifyQueryValue(path),
    forceRefresh: undefined,
    isIframe: undefined,
    restOfQuery,
  };

  if (forceRefresh === 'true') {
    propsFromRouter.forceRefresh = true;
  } else if (forceRefresh === 'false') {
    propsFromRouter.forceRefresh = false;
  }

  if (isIframe === 'true') {
    propsFromRouter.isIframe = true;
  } else if (forceRefresh === 'false') {
    propsFromRouter.isIframe = false;
  }

  useEffect(() => {
    if (isAuthenticated && propsFromRouter.workspaceId) {
      getWorkspaceLaunchUrl(
        propsFromRouter.workspaceId,
        propsFromRouter.path,
        propsFromRouter.forceRefresh,
        propsFromRouter.isIframe
      )
        .then((response) => {
          if (response.url) {
            setLaunchUrl(response.url);
            // We want to avoid creating a browser history entry for this.
            window.location.replace(
              constructLaunchUrl(response.url, {
                ...propsFromRouter.restOfQuery,
              })
            );
          }
        })
        .catch(() => {
          setHasError(true);
        });
    }
  }, [
    isAuthenticated,
    propsFromRouter.workspaceId,
    propsFromRouter.path,
    propsFromRouter.forceRefresh,
    propsFromRouter.isIframe,
    propsFromRouter.restOfQuery,
  ]);

  return {
    launchUrl,
    hasError,
    setHasError,
  };
};

export const AuthenticationPage = () => {
  const isAuthenticated = isAuthenticatedUser();
  const location = useLocation();
  const { launchUrl, hasError, setHasError } = useWorkspaceLaunchUrl();

  if (hasError) {
    return (
      <LoadingSection
        hasError
        data-test="authentication-failed"
        label={_t(
          "Failed to authenticate to this Lab. Please make sure that you're logged in to Coursera and that you have access to this Lab."
        )}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthenticationModal
        redirectTo={location.pathname}
        // In order for this to make the hasError return block actually execute, the hasError return block needs to be located above this !isAuthenticated return block.
        onClose={() => setHasError(true)}
      />
    );
  }

  if (launchUrl) {
    return <LoadingSection label={_t('Taking you to your lab...')} />;
  }

  return <LoadingSection label={_t('Generating token...')} />;
};

export default AuthenticationPage;
